import { useMutation, useQueryClient } from "react-query";

import { moveTarget } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS, GET_TARGET_BY_ID } from "@constants/reactQueryKeys";

interface MoveTargetPayload {
  targetId: number;
  /** Current parent goal id — used to invalidate the source list. */
  sourceGoalId: number;
  /** Destination goal id — pre-validated by the caller. */
  destinationGoalId: number;
}

/**
 * Move a target to a different goal. The caller must pre-filter the
 * destination list to: same behavior_subject, same goal_type, not archived.
 * Both the source and destination goal target lists are invalidated.
 */
const useMoveTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: moveTargetAction,
    isLoading: isMovingTarget,
    ...rest
  } = useMutation(
    ({ targetId, destinationGoalId }: MoveTargetPayload) =>
      moveTarget(targetId, destinationGoalId),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Target moved" });
        queryClient.invalidateQueries([GET_TARGET_BY_ID, variables.targetId]);
        queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.sourceGoalId]);
        queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.destinationGoalId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.sourceGoalId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.destinationGoalId]);
        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to move target";
        showToast({ message: errorMessage, errorState: true });
        onError(error);
      },
      ...otherOptions,
    },
  );

  return { moveTargetAction, isMovingTarget, ...rest };
};

export default useMoveTarget;
